import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { compose } from "redux";
import { find, get } from "lodash";
import { withStyles } from "@mui/styles";
import {
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  TableFooter,
  InputLabel,
  Input,
  FormControl,
  IconButton,
  Button,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Resource from "common/components/Resource";
import Pagination from "common/components/Pagination";
import { ClickableTableCell } from "common/components/Table";
import EnhancedCard from "common/components/EnhancedCard";
import { withUrlResource } from "common/utils";
import { Translation } from "react-i18next";

const categoryValue = (categories, key) =>
  get(find(categories, { key }), "value", key);

class ServiceList extends Component {
  state = {
    categoryFilter: "",
  };

  handleFilterChange = (e) => this.setState({ categoryFilter: e.target.value });

  render() {
    const { classes, match, history, urlResource } = this.props;
    const { categoryFilter } = this.state;
    const queryParams = {};
    if (categoryFilter) {
      queryParams.category = categoryFilter;
    }

    return (
      <Translation>
        {(t) => (
          <EnhancedCard
            title={t("services_list.title")}
            cardContentRoot={{ padding: 0 }}
            cardHeaderStyle={{ fontSize: "1.25rem" }}
            action={
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  history.push(
                    `/${urlResource.singular}/${urlResource.id}/services/new`,
                  );
                }}
              >
                {t("services_list.add_btn")}
              </Button>
            }
          >
            <Resource
              name="service_categories"
              render={(categories) => (
                <Fragment>
                  <FormControl className={classes.formControl}>
                    <InputLabel htmlFor="category-filter">
                      {t("services_list.filter_label")}
                    </InputLabel>
                    <Select
                      value={categoryFilter}
                      onChange={this.handleFilterChange}
                      input={
                        <Input name="category-filter" id="category-filter" />
                      }
                    >
                      <MenuItem value="">
                        <em>{t("services_list.filter_option_all")}</em>
                      </MenuItem>
                      {categories.map((category) => (
                        <MenuItem value={category.key} key={category.key}>
                          {category.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Resource
                    name={`${urlResource.plural}/${urlResource.id}/services`}
                    queryParams={queryParams}
                    error={() => (
                      <Typography>{t("services_list.error")}</Typography>
                    )}
                    render={(
                      services,
                      { prevPage, nextPage, page, removeOne },
                    ) => (
                      <Table className={classes.table}>
                        <TableHead>
                          <TableRow>
                            <TableCell classes={{ root: classes.th }}>
                              {t("services_list.th_id")}
                            </TableCell>
                            <TableCell classes={{ root: classes.th }}>
                              {t("services_list.th_title")}
                            </TableCell>
                            <TableCell classes={{ root: classes.th }}>
                              {t("services_list.th_category")}
                            </TableCell>
                            <TableCell classes={{ root: classes.th }}>
                              {t("services_list.th_delete")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {services.length ? (
                            services.map((service) => (
                              <TableRow hover key={service.id}>
                                <TableCell className={classes.td}>
                                  {service.id}
                                </TableCell>
                                <ClickableTableCell
                                  style={{
                                    color: "#000",
                                    textDecoration: "underline",
                                  }}
                                  onClick={() =>
                                    history.push(
                                      `${match.url}/edit/${service.id}`,
                                    )
                                  }
                                >
                                  {service.title}
                                </ClickableTableCell>
                                <TableCell className={classes.td}>
                                  {categoryValue(categories, service.category)}
                                </TableCell>
                                <TableCell className={classes.td}>
                                  <IconButton style={{ padding: 0 }}>
                                    <DeleteIcon
                                      onClick={() => removeOne(service.id)}
                                    />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell className={classes.td} colSpan={5}>
                                {t("services_list.no_records")}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                        <TableFooter>
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className={classes.pagination}
                            >
                              <Pagination
                                page={page}
                                nextPage={nextPage}
                                prevPage={prevPage}
                                items={services}
                              />
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    )}
                  />
                </Fragment>
              )}
            />
          </EnhancedCard>
        )}
      </Translation>
    );
  }
}

const styles = (theme) => ({
  table: {
    minWidth: 700,
    padding: 0,
  },
  th: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    backgroundColor: "#7f7f7f",
    color: "#fff",
    fontWeight: "bold",
  },
  td: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  pagination: {
    textAlign: "right",
  },
  formControl: {
    margin: theme.spacing(2),
    minWidth: 120,
  },
});

ServiceList.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  urlResource: PropTypes.shape({
    singular: PropTypes.string.isRequired,
    plural: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
  }).isRequired,
};

const enhanced = compose(withStyles(styles), withUrlResource);

export default enhanced(ServiceList);
